/**
 * Environment configuration
 * Centralizes all environment-specific settings
 */

// ========================================
// SIMPLE BACKEND TOGGLE
// Change this to easily switch backends:
// true = Use Production Backend (https://draamview.com/api/v1)
// false = Use Local Backend (http://127.0.0.1:8000/api/v1)
// ========================================
const USE_PRODUCTION_BACKEND = true;

// Environment types
export type Environment = 'development' | 'staging' | 'production';

// Determine current environment
export const getEnvironment = (): Environment => {
  const env = process.env.NEXT_PUBLIC_APP_ENV || 'development';
  
  if (env === 'production' || env === 'staging' || env === 'development') {
    return env;
  }
  
  // Default to development if invalid environment is specified
  console.warn(`Invalid environment "${env}" specified, defaulting to "development"`);
  return 'development';
};

// Get API URL based on simple toggle (overrides everything)
export const getApiUrl = (): string => {
  // PRIORITY 1: Simple toggle - overrides everything
  const apiUrl = USE_PRODUCTION_BACKEND 
    ? 'https://draamview.com/api/v1' 
    : 'http://127.0.0.1:8000/api/v1';
  
  // Log which backend is being used in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`🎯 Backend Toggle: ${USE_PRODUCTION_BACKEND ? 'PRODUCTION' : 'LOCAL'} → ${apiUrl}`);
  }
  
  return apiUrl;
};

// Praj user routing: this user's broker login/order traffic must originate from
// the praj machine (3.109.99.9) because the broker has whitelisted that IP.
// All other users must use the HFT machine.
const PRAJ_USER_ID = 25;
// Google-login users are stored with phone_number nulled and a temp handle of
// EMAIL_<local-part>_<n>, so the local part is the stable email identifier.
const PRAJ_EMAIL_LOCAL_PART = 'prajnavatllp';
const PRAJ_NAME_FRAGMENT = 'prajnavat';
const PRAJ_BROKER_PLATFORM_URL = 'https://praj.draamview.com/api/v1';
const HFT_BROKER_PLATFORM_URL = 'https://hft.draamview.com/api/v1';

// Matches on id, email or name: user_data is written by two paths (login stores
// user_id, token validation stores id), so a single key is not enough.
const isPrajUser = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem('user_data');
    if (!raw) return false;
    const storedUser = JSON.parse(raw) as {
      id?: number | string;
      user_id?: number | string;
      email?: string | null;
      first_name?: string | null;
      last_name?: string | null;
    };

    const identifier = storedUser.id ?? storedUser.user_id;
    if (identifier !== undefined && Number(identifier) === PRAJ_USER_ID) return true;

    const email = storedUser.email?.toLowerCase() ?? '';
    if (email.startsWith(`${PRAJ_EMAIL_LOCAL_PART}@`)) return true;

    const fullName = `${storedUser.first_name ?? ''} ${storedUser.last_name ?? ''}`.toLowerCase();
    if (fullName.includes(PRAJ_NAME_FRAGMENT)) return true;
  } catch {
    return false;
  }
  return false;
};

// Get Broker Platform API URL.
// Resolved per-call so it can honor per-user routing (praj vs hft).
export const getBrokerPlatformApiUrl = (): string => {
  const envUrl = process.env.NEXT_PUBLIC_BROKER_PLATFORM_API_URL;
  const defaultUrl = envUrl
    ? envUrl
    : USE_PRODUCTION_BACKEND
      ? HFT_BROKER_PLATFORM_URL
      : 'http://127.0.0.1:8001/api/v1';

  // Only override in production-style routing; in local dev keep the local backend.
  if (isPrajUser() && (USE_PRODUCTION_BACKEND || envUrl)) {
    return PRAJ_BROKER_PLATFORM_URL;
  }
  return defaultUrl;
};
